/**
 * Helpers SEO de servicio: título, meta description, canonical y copia semántica.
 * Funciones puras y aisladas: no tocan el DOM ni el estado de reserva.
 */
import { slugForService } from '@/services/seo/serviceSlugMap';
import { loadBranding } from '@/services/branding.service';

export interface SeoService {
  id: number;
  name: string;
  duration_blocks?: number;
  block_size_minutes?: number | null;
  description?: string | null;
  price?: number;
}

export const DEFAULT_BLOCK_MINUTES = 30;

/**
 * Bare host of the platform. There is exactly one and it is never a brand's own
 * subdomain: a brand is reached by path (`bioterapias.cl/<slug>`), so the layout
 * publishes the apex here and the brand's prefix separately in `pathPrefix`.
 */
export function siteDomain(): string {
  const fromPayload = loadBranding().domain;
  if (fromPayload) return fromPayload;
  // The SPA always knows the host it was served from, so the canonical never
  // degrades to a relative/empty domain when the payload is missing.
  return typeof window !== 'undefined' ? window.location.host : '';
}

/**
 * Absolute base every URL this module publishes: apex host plus the brand's path
 * prefix. Without the prefix a brand would publish the apex's URL for its own
 * pages, which is how two brands end up competing for one canonical.
 */
export function brandBaseUrl(): string {
  return `https://${siteDomain()}${loadBranding().pathPrefix ?? ''}`;
}

export function siteName(): string {
  return loadBranding().siteName;
}

export function serviceArea(): string {
  return loadBranding().serviceArea || '';
}

export function bookingUrl(): string {
  return `${brandBaseUrl()}/agendar`;
}

/** " en Vitacura" on brand hosts, "" on hosts without a service area. */
function inArea(): string {
  const area = serviceArea();
  return area ? ` en ${area}` : '';
}

export function formatPrice(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function durationMinutes(service: SeoService): number {
  const blocks = service.duration_blocks || 1;
  const size = service.block_size_minutes || DEFAULT_BLOCK_MINUTES;
  return blocks * size;
}

export function durationText(service: SeoService): string {
  const total = durationMinutes(service);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours > 0) {
    const hourLabel = `${hours} hora${hours > 1 ? 's' : ''}`;
    return minutes > 0 ? `${hourLabel} ${minutes} minutos` : hourLabel;
  }
  return `${minutes} minutos`;
}

/** ISO-8601 duration (PT1H30M) para el esquema schema.org `duration`. */
export function isoDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hPart = hours > 0 ? `${hours}H` : '';
  const mPart = rest > 0 ? `${rest}M` : '';
  return `PT${hPart}${mPart}`;
}

export function buildServicePath(service: SeoService): string {
  return `/servicios/${slugForService(service)}`;
}

export function buildCanonicalUrl(service: SeoService): string {
  return `${brandBaseUrl()}${buildServicePath(service)}`;
}

export function buildServiceTitle(service: SeoService): string {
  return `${service.name}${inArea()} | ${siteName()}`;
}

function truncateTo(value: string, max: number): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  const sliced = normalized.slice(0, max - 1).trimEnd();
  return `${sliced}…`;
}

export function buildServiceDescription(service: SeoService): string {
  const duration = durationText(service);
  const price = service.price && service.price > 0 ? `desde $${formatPrice(service.price)}` : '';
  const parts = [
    `Agenda tu cita de ${service.name}${inArea()}: reserva online en ${bookingUrl()}.`,
    `Atención de ${duration}.`,
    price,
    'Pies sanos, cómodos e impecables.',
  ];
  return truncateTo(parts.filter(Boolean).join(' '), 155);
}

export interface SeoCopySection {
  heading: string;
  body: string;
}

export interface SeoCopy {
  intro: string;
  sections: SeoCopySection[];
}

/** Copia semántica (h1/h2/p) para densidad de keywords locales. */
export function buildServiceSeoCopy(service: SeoService): SeoCopy {
  const duration = durationText(service);
  const price =
    service.price && service.price > 0
      ? `desde $${formatPrice(service.price)}`
      : 'con precios accesibles';
  const name = service.name;

  return {
    intro:
      `${name}${inArea()}. ` +
      (service.description ||
        `Servicio profesional de ${name} realizado por especialistas certificados en el estudio. `) +
      `Reserva tu cita online en minutos en ${bookingUrl()} y asegura tu horario.`,
    sections: [
      {
        heading: `¿En qué consiste ${name}?`,
        body:
          (service.description || `Técnica profesional de ${name} con resultados impecables`) +
          ', aplicada con instrumental estéril y un protocolo de higiene riguroso para cuidar la salud de tus pies.',
      },
      {
        heading: 'Duración de la sesión',
        body: `Una sesión de ${name} toma aproximadamente ${duration}. Tu profesional te atiende de forma personalizada y verifica el resultado contigo antes de finalizar.`,
      },
      {
        heading: 'Materiales y cuidado durante la atención',
        body: `Usamos instrumental estéril y productos dermatológicos de marcas profesionales, con desinfección certificada de todos los instrumentos antes de cada atención.`,
      },
      {
        heading: 'Cuidados posteriores',
        body: `Para prolongar los resultados de tu ${name}, mantén la hidratación de tus pies a diario, usa calzado cómodo y sigue las recomendaciones de tu podólogo.`,
      },
      {
        heading: serviceArea() ? `Agenda tu ${name} en ${serviceArea()}` : `Agenda tu ${name}`,
        body:
          `Elige horario, profesional y confirma en segundos ${price}. ` +
          (loadBranding().address
            ? `Atendemos en ${loadBranding().address}, con atención personalizada.`
            : 'Atención personalizada en el centro.'),
      },
      {
        heading: 'Reserva online tu hora',
        body: `Agendar es gratis y toma menos de un minuto. Entra a ${bookingUrl()}, elige tu ${name}, el profesional, la fecha y el horario que prefieras, y confirma tu cita al instante sin pagar por adelantado. Ante cualquier consulta, escríbenos por nuestro chat o WhatsApp.`,
      },
    ],
  };
}
